// Open Trade Packet email (ADR_LOS-026): one .eml per master trade covering
// the selected sub-trades, with the two doc-group zips attached. Reuses the
// LOS-5519 .eml pattern — X-Unsent: 1 (opens as an Outlook draft), no From:.
import {
  escapeHtml,
  formatEmailDate,
  sanitizeBoundaryId,
  sanitizeHeaderValue,
} from './trade-eml-builder'

const utf8ToBase64 = (s: string): string => btoa(unescape(encodeURIComponent(s)))

const formatCurrency = (raw: string): string => {
  const n = Number(raw)
  return isNaN(n)
    ? escapeHtml(raw)
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export type OpenTradeEmlAttachment = {
  fileName: string
  base64: string
  mimeType: string
}

export type OpenTradeEmlParams = {
  agentEntityName: string
  companyName: string
  dealName: string
  dealCode: string
  masterTradeSystemId: string
  tradeDate: string
  facilityNames: string[]
  tcAmount: string
  aaAmount: string
  sellers: string[]
  buyers: string[]
  selectedCount: number
  totalCount: number
  attachedDocGroups: string[]
  to: string[]
  cc?: string[]
  operationsEmail: string
  attachments: OpenTradeEmlAttachment[]
}

export const buildOpenTradeEml = (p: OpenTradeEmlParams): string => {
  const boundary = `----=_LENDOS_${sanitizeBoundaryId(p.masterTradeSystemId) || 'TRADE'}`
  const subject = sanitizeHeaderValue(
    `(${p.agentEntityName}) ${p.companyName} Internal Transfer - ${p.dealName} - ${p.masterTradeSystemId}`,
  )

  const dealLine = p.dealCode ? `${p.dealName} (${p.dealCode})` : p.dealName
  const html = [
    '<html><body style="font-family:Arial,Helvetica,sans-serif;font-size:13px;">',
    `<p>Hi ${escapeHtml(p.agentEntityName)},</p>`,
    `<p>Please review the attached docs (${escapeHtml(p.attachedDocGroups.join(', '))}) for settlement using a one-day lead.</p>`,
    '<p>Please note that parties would like to expedite settlement using a one-day lead and LendOS has a 3PM EST deadline for return receipts.</p>',
    '<p>Please let us know if you have any issues.</p>',
    `<p><b>${p.selectedCount} of ${p.totalCount} allocations</b></p>`,
    '<p><b>Seller(s):</b><br>',
    p.sellers.map(escapeHtml).join('<br>'),
    '</p>',
    '<p><b>Buyer:</b><br>',
    p.buyers.map(escapeHtml).join('<br>'),
    '</p>',
    '<p>',
    `<b>Deal:</b> ${escapeHtml(dealLine)}<br>`,
    `<b>Trade Date:</b> ${formatEmailDate(p.tradeDate)}`,
    '</p>',
    '<p>',
    `<b>Facility:</b> ${escapeHtml(p.facilityNames.join(', '))}<br>`,
    `<b>TC Amount:</b> $ ${formatCurrency(p.tcAmount)}<br>`,
    `<b>AA Amount:</b> $ ${formatCurrency(p.aaAmount)}`,
    '</p>',
    `<p>Please email <a href="mailto:${escapeHtml(p.operationsEmail)}">${escapeHtml(p.operationsEmail)}</a>`,
    ' if you have any questions regarding the above information.</p>',
    '<p style="font-size:11px;color:#888;"><b>Confidentiality Notice:</b></p>',
    '<p style="font-size:11px;color:#888;">This email may contain privileged, confidential, and/or sensitive information intended solely for the use of the designated recipient. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or use of the contents of this transmission is strictly prohibited. If you have received this communication in error, please notify the sender immediately and permanently delete all copies, whether electronic or physical.</p>',
    '<p style="font-size:11px;color:#888;">This communication may include nonpublic personal information (NPI) subject to the privacy and data protection requirements of the Gramm-Leach-Bliley Act (GLBA) and the Sarbanes-Oxley Act (SOX). Such information may not be reused or disclosed for any purpose other than to perform the services for which it was provided. Please note that electronic communications involve inherent risks. LendOS does not control the method of transmission or any third-party service providers used in connection with this message and assumes no liability for any delays, interceptions, or unauthorized access.</p>',
    '</body></html>',
  ].join('')

  const headers = [
    'MIME-Version: 1.0',
    'Date: ' + new Date().toUTCString(),
    `To: ${sanitizeHeaderValue(p.to.join('; '))}`,
    ...(p.cc?.length ? [`Cc: ${sanitizeHeaderValue(p.cc.join('; '))}`] : []),
    `Subject: ${subject}`,
    'X-Unsent: 1',
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
  ].join('\r\n')

  const bodyPart = [
    `--${boundary}`,
    'Content-Type: text/html; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    utf8ToBase64(html).replace(/(.{76})/g, '$1\r\n'),
  ].join('\r\n')

  const attachmentParts = p.attachments.map(a =>
    [
      `--${boundary}`,
      `Content-Type: ${a.mimeType}; name="${a.fileName}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${a.fileName}"`,
      '',
      a.base64.replace(/(.{76})/g, '$1\r\n'),
    ].join('\r\n'),
  )

  return [headers, '', bodyPart, ...attachmentParts, `--${boundary}--`, ''].join('\r\n')
}
